import { useEffect, useRef, useState, useCallback } from 'react'
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useAdventureEditorStore, selectCategorySettings } from '../../stores/adventureEditorStore'
import { AdventureRibbon, SectionId, VALID_SECTION_IDS } from './AdventureRibbon'
import {
  AdventureContentPanels,
  AdventureContentPanelsRef,
} from './AdventureContentPanels'
import type { AdventureDto } from '../../types/adventure-editor.types'
import { isGeoCacheCategory, isTrailCategory, AdventureMainCategory } from '../../types/adventure-editor.types'
import { isWorkflowAllowed } from '../../types/category.types'
import { useWorkflowStore } from '@store/workflowStore'
import { WorkflowNodeType } from '../../types/workflow.types'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'
import { adventuresApi } from '@api/endpoints/adventures'
import { workflowDesignerApi } from '@api/endpoints/workflowDesigner'
import { useTags } from '@contexts/TagsContext'
import { AdventureCollaborationProvider } from '@contexts/AdventureCollaborationContext'
import { CollaboratorsIndicator } from './CollaboratorsIndicator'
import { FieldChangeIndicatorProvider } from './FieldChangeIndicator'
import { AdventureHistoryDialog } from './AdventureHistoryDialog'
import { FlowVersionHistoryDialog } from './FlowVersionHistoryDialog'
import { useAdventureCategories } from '../../hooks/useAdventures'
import { useCollaborativeAdventureEditor } from '@hooks/useCollaborativeAdventureEditor'
import { usePublishReadiness } from '../../hooks/usePublishReadiness'

/**
 * Component that activates the collaborative editor hook.
 * Must be rendered inside AdventureCollaborationProvider.
 * This ensures remote changes are always received regardless of which panel is active.
 */
function CollaborativeEditorReceiver() {
  useCollaborativeAdventureEditor()
  return null
}
import './styles/AdventureEditor.css'

// ====================
// TYPES
// ====================

interface AdventureEditorProps {
  adventureId?: string
  existingAdventure?: AdventureDto
  onSave?: (adventure: any) => Promise<void>
  onPublish?: (adventure: any) => Promise<void>
  onDelete?: () => void
  onCancel?: () => void
}

// ====================
// COMPONENT
// ====================

export function AdventureEditor({
  adventureId,
  existingAdventure,
  onSave,
  onPublish,
  onDelete,
  onCancel,
}: AdventureEditorProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const contentPanelsRef = useRef<AdventureContentPanelsRef>(null)
  const flowImportInputRef = useRef<HTMLInputElement>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [flowVersionHistoryOpen, setFlowVersionHistoryOpen] = useState(false)

  // Use cached tags from context - get only Adventure tags
  const { getAdventureCategories, isLoading: isLoadingTags } = useTags()

  // Fetch adventure categories (for category settings)
  const { data: adventureCategories } = useAdventureCategories()

  // Store state
  const adventure = useAdventureEditorStore((state) => state.adventure)
  const generalInfo = useAdventureEditorStore((state) => state.generalInfo)
  const activeSection = useAdventureEditorStore((state) => state.activeSection)
  const isLoading = useAdventureEditorStore((state) => state.isLoading)
  const isDirty = useAdventureEditorStore((state) => state.isDirty)
  const loadAdventure = useAdventureEditorStore((state) => state.loadAdventure)
  const loadCategorySettings = useAdventureEditorStore((state) => state.loadCategorySettings)
  const setActiveSection = useAdventureEditorStore((state) => state.setActiveSection)
  const setIsLoading = useAdventureEditorStore((state) => state.setIsLoading)
  const reset = useAdventureEditorStore((state) => state.reset)
  const loadAvailableCategories = useAdventureEditorStore((state) => state.loadAvailableCategories)
  const setIsLoadingTags = useAdventureEditorStore((state) => state.setIsLoadingTags)
  const useDisplayForJoining = useAdventureEditorStore((state) => state.useDisplayForJoining)
  const categorySettings = useAdventureEditorStore(selectCategorySettings)

  // Load cached adventure tags into store when available
  useEffect(() => {
    const tagCategories = getAdventureCategories()
    if (tagCategories && tagCategories.length > 0) {
      const state = useAdventureEditorStore.getState()
      // Only load if store doesn't already have categories
      if (state.availableCategories.length === 0) {
        loadAvailableCategories(tagCategories)
      }
    }
    setIsLoadingTags(isLoadingTags)
  }, [getAdventureCategories, isLoadingTags, loadAvailableCategories, setIsLoadingTags])

  // Load category settings when mainCategory or available categories change
  useEffect(() => {
    if (adventureCategories && generalInfo.mainCategory) {
      const matchingCategory = adventureCategories.find(
        (cat) => cat.id === generalInfo.mainCategory
      )
      loadCategorySettings(matchingCategory || null)
    }
  }, [adventureCategories, generalInfo.mainCategory, loadCategorySettings])

  // Helper to get section from URL hash
  const getSectionFromHash = useCallback((): SectionId | null => {
    const hash = window.location.hash.slice(1) // Remove the '#'
    if (hash && VALID_SECTION_IDS.includes(hash as SectionId)) {
      return hash as SectionId
    }
    return null
  }, [])

  // Read hash on mount and set initial section
  useEffect(() => {
    const sectionFromHash = getSectionFromHash()
    if (sectionFromHash) {
      setActiveSection(sectionFromHash)
    }
  }, [getSectionFromHash, setActiveSection])

  // Listen for browser back/forward navigation (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const sectionFromHash = getSectionFromHash()
      if (sectionFromHash) {
        setActiveSection(sectionFromHash)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [getSectionFromHash, setActiveSection])

  // Load adventure on mount
  useEffect(() => {
    if (existingAdventure) {
      loadAdventure(existingAdventure)
    } else if (adventureId) {
      // TODO: Fetch adventure from API
      setIsLoading(true)
      // fetchAdventure(adventureId).then(loadAdventure).finally(() => setIsLoading(false))
    }

    // Cleanup on unmount
    return () => {
      reset()
    }
  }, [adventureId, existingAdventure])

  // Handle section change (extract editor content before switching)
  const handleSectionChange = async (newSection: SectionId) => {
    if (activeSection === 'description' && contentPanelsRef.current) {
      try {
        await contentPanelsRef.current.getDescriptionEditorContent()
      } catch (error) {
        console.error('Error getting editor content:', error)
      }
    }

    setActiveSection(newSection)

    // Update URL hash for navigation (use replaceState to avoid cluttering history)
    const newUrl = `${window.location.pathname}${window.location.search}#${newSection}`
    window.history.replaceState(null, '', newUrl)
  }

  // Validate required tags
  const validateRequiredTags = (): { isValid: boolean; missingCategories: string[] } => {
    const state = useAdventureEditorStore.getState()
    const availableCategories = state.availableCategories
    const selectedTags = adventure?.tags || []

    console.log('🔍 Validation Debug:', {
      availableCategoriesCount: availableCategories.length,
      selectedTagsCount: selectedTags.length,
      selectedTags,
      adventure: adventure,
    })

    // If tags haven't loaded yet, fail validation
    if (availableCategories.length === 0) {
      console.log('⚠️ Tags not loaded yet!')
      return {
        isValid: false,
        missingCategories: ['Tags are still loading, please wait...'],
      }
    }

    const missingCategories: string[] = []

    // Check all adventure tag categories
    availableCategories.forEach((category) => {
      console.log(`🔎 Checking category "${category.displayName}":`, {
        selectionRequired: category.selectionRequired,
        isActive: category.isActive,
        tagIds: category.tags.map(t => t.id),
        selectedTags,
      })

      if (category.selectionRequired && category.isActive) {
        const hasSelection = category.tags.some((tag) => selectedTags.includes(tag.id))
        console.log(`📋 Category "${category.displayName}" - Required: ${category.selectionRequired}, Has Selection: ${hasSelection}`)
        if (!hasSelection) {
          missingCategories.push(category.displayName)
        }
      }
    })

    console.log('❌ Missing Required Categories:', missingCategories)

    return {
      isValid: missingCategories.length === 0,
      missingCategories,
    }
  }

  // Handle save
  const handleSave = async () => {
    setError(null)
    setIsSaving(true)

    try {
      // Validate required tags
      console.log('💾 Starting save validation...')
      const tagValidation = validateRequiredTags()
      if (!tagValidation.isValid) {
        console.error('🚫 Validation failed! Missing categories:', tagValidation.missingCategories)
        throw new Error(
          `Please select tags for the following required categories: ${tagValidation.missingCategories.join(', ')}`
        )
      }
      console.log('✅ Validation passed!')

      // Extract editor content first
      if (contentPanelsRef.current) {
        await contentPanelsRef.current.getDescriptionEditorContent()
      }

      // Compile adventure data
      const adventureData = {
        id: adventure?.id || adventureId,
        ...generalInfo,
        tags: adventure?.tags || [],
        useDisplayForJoining,
        // Include image URLs and IDs from adventure object
        thumbnailImageUrl: adventure?.thumbnailImageUrl,
        coverImageUrl: adventure?.coverImageUrl,
        thumbnailImageId: adventure?.thumbnailImageId,
        coverImageId: adventure?.coverImageId,
      }

      console.log('📦 Adventure data being saved:', adventureData)
      console.log('📦 Tags being saved:', adventureData.tags)
      console.log('📦 Adventure object:', adventure)

      // Save adventure general info
      if (onSave) {
        await onSave(adventureData)
      } else {
        // TODO: Default save to API
        console.log('Saving adventure:', adventureData)
      }

      // Save tags separately
      const adventureIdToSave = adventure?.id || adventureId
      if (adventureIdToSave && adventureData.tags.length >= 0) {
        console.log('💾 Saving tags separately:', adventureData.tags)
        await adventuresApi.saveTags(adventureIdToSave, adventureData.tags)
        console.log('✅ Tags saved successfully!')

        // Tags are already in the store and will be included when adventure is reloaded
      }
    } catch (err: unknown) {
      console.error('🔥 Save error caught:', err)
      const friendlyError = getSaveFriendlyError(err)
      console.error('🔥 Friendly error:', friendlyError)
      setError(friendlyError)
    } finally {
      setIsSaving(false)
    }
  }

  // Handle publish
  const handlePublish = async () => {
    setError(null)
    setIsPublishing(true)

    try {
      // Validate required fields
      if (!generalInfo.title || generalInfo.title.length < 3) {
        throw new Error('Please enter a valid title (at least 3 characters)')
      }

      // Validate required tags
      console.log('📢 Starting publish validation...')
      const tagValidation = validateRequiredTags()
      if (!tagValidation.isValid) {
        console.error('🚫 Publish validation failed! Missing categories:', tagValidation.missingCategories)
        throw new Error(
          `Please select tags for the following required categories: ${tagValidation.missingCategories.join(', ')}`
        )
      }
      console.log('✅ Publish validation passed!')

      // Validate workflow for Trail adventures
      if (isTrailCategory(generalInfo.mainCategory)) {
        const workflowState = useWorkflowStore.getState()
        // Trails require at least one node beyond the start node
        const hasWorkflow = workflowState.nodes && workflowState.nodes.length > 1
        if (!hasWorkflow) {
          throw new Error(
            'Trail adventures require a workflow with at least one waypoint or checkpoint. Please add waypoints in the Flow editor.'
          )
        }
        console.log('✅ Trail workflow validation passed!')
      }

      // Extract editor content first
      if (contentPanelsRef.current) {
        await contentPanelsRef.current.getDescriptionEditorContent()
      }

      // Compile adventure data
      const adventureData = {
        id: adventure?.id || adventureId,
        ...generalInfo,
        tags: adventure?.tags || [],
        status: 'Published',
        // Include image URLs and IDs from adventure object
        thumbnailImageUrl: adventure?.thumbnailImageUrl,
        coverImageUrl: adventure?.coverImageUrl,
        thumbnailImageId: adventure?.thumbnailImageId,
        coverImageId: adventure?.coverImageId,
      }

      // Save adventure general info with Published status
      if (onPublish) {
        await onPublish(adventureData)
      } else {
        // TODO: Default publish to API
        console.log('Publishing adventure:', adventureData)
      }

      // Save tags separately
      const adventureIdToSave = adventure?.id || adventureId
      if (adventureIdToSave && adventureData.tags.length >= 0) {
        console.log('💾 Saving tags separately:', adventureData.tags)
        await adventuresApi.saveTags(adventureIdToSave, adventureData.tags)
        console.log('✅ Tags saved successfully!')

        // Tags are already in the store and will be included when adventure is reloaded
      }
    } catch (err: unknown) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsPublishing(false)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      // Default navigation
      window.history.back()
    }
  }

  // Handle history dialog
  const handleHistory = () => {
    setHistoryDialogOpen(true)
  }

  const handleHistoryClose = () => {
    setHistoryDialogOpen(false)
  }

  // Handle flow version history dialog
  const handleFlowVersionHistory = () => {
    setFlowVersionHistoryOpen(true)
  }

  const handleFlowVersionHistoryClose = () => {
    setFlowVersionHistoryOpen(false)
  }

  // Get publish readiness from hook (includes all validation checks)
  const { isReady: isReadyToPublish } = usePublishReadiness()

  // Check if workflow has any map-relevant nodes (PoiLocation or Route)
  const workflowNodes = useWorkflowStore((state) => state.nodes)
  const hasMapNodes = workflowNodes.some(
    (node) =>
      node.data.nodeType === WorkflowNodeType.PoiLocation ||
      node.data.nodeType === WorkflowNodeType.Route
  )

  // Workflow store functions for preloading and import
  const loadWorkflow = useWorkflowStore((state) => state.loadWorkflow)
  const loadFromJson = useWorkflowStore((state) => state.loadFromJson)
  const initializeEmptyWorkflow = useWorkflowStore((state) => state.initializeEmptyWorkflow)
  const setWorkflowAdventureId = useWorkflowStore((state) => state.setAdventureId)
  const workflowStoreAdventureId = useWorkflowStore((state) => state.adventureId)

  // Handle flow import - trigger file input click
  const handleImportFlow = useCallback(() => {
    flowImportInputRef.current?.click()
  }, [])

  // Handle flow import file selection
  const handleFlowFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        const data = JSON.parse(content)
        loadFromJson(data)
      } catch (err) {
        console.error('Failed to import workflow JSON:', err)
        setError({
          title: 'Import Failed',
          message: 'Failed to parse the JSON file. Please ensure it is a valid workflow export.',
        })
      }
    }
    reader.readAsText(file)

    // Reset input so same file can be selected again
    event.target.value = ''
  }, [loadFromJson])

  // Determine if workflow is allowed for this category
  const workflowAllowedForCategory = !(
    categorySettings?.workflowAllowed === false ||
    (categorySettings === null && generalInfo.mainCategory && !isWorkflowAllowed(generalInfo.mainCategory))
  )

  // Preload workflow data when adventure editor opens (if workflow is allowed)
  const effectiveAdventureId = adventure?.id || adventureId
  const { data: preloadedWorkflow, isError: workflowError, isFetching: workflowFetching } = useQuery({
    queryKey: ['workflow', effectiveAdventureId],
    queryFn: () => workflowDesignerApi.getFlow(effectiveAdventureId!),
    retry: false,
    enabled: !!effectiveAdventureId && workflowAllowedForCategory,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  // Load preloaded workflow into store
  useEffect(() => {
    if (!effectiveAdventureId || !workflowAllowedForCategory) return

    // Set adventure ID in workflow store if not already set
    if (workflowStoreAdventureId !== effectiveAdventureId) {
      setWorkflowAdventureId(effectiveAdventureId)
    }

    // Load workflow data into store when fetched
    if (preloadedWorkflow) {
      loadWorkflow(preloadedWorkflow)
    } else if (!workflowFetching && (workflowError || preloadedWorkflow === null)) {
      // No workflow exists - initialize with default Start node
      initializeEmptyWorkflow()
    }
  }, [
    effectiveAdventureId,
    workflowAllowedForCategory,
    preloadedWorkflow,
    workflowFetching,
    workflowError,
    workflowStoreAdventureId,
    setWorkflowAdventureId,
    loadWorkflow,
    initializeEmptyWorkflow,
  ])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // On mobile, hide the ribbon when in locationflow (workflow editor has its own hamburger menu)
  const showRibbon = !(isMobile && activeSection === 'locationflow')

  // Hidden sections list for the current adventure configuration
  // QRCode criteria type ID is 402
  const QR_CODE_CRITERIA = 402
  const hasQrCodeCriteria = categorySettings?.allowedJoinCriteria?.includes(QR_CODE_CRITERIA) ||
    categorySettings?.allowedDisplayCriteria?.includes(QR_CODE_CRITERIA)
  const isSilentAdventure = categorySettings?.id === 10 ||
    generalInfo.mainCategory === AdventureMainCategory.SilentAdventure

  const computedHiddenSections: SectionId[] = [
    ...(useDisplayForJoining ? ['join'] as const : []),
    ...(!generalInfo.allowGroups || isGeoCacheCategory(generalInfo.mainCategory) || categorySettings?.groupsAllowed === false ? ['groups'] as const : []),
    ...(!workflowAllowedForCategory ? ['locationflow', 'mapview'] as const : []),
    ...(workflowAllowedForCategory && !hasMapNodes ? ['mapview'] as const : []),
    ...(!workflowAllowedForCategory ? ['testers', 'maintainers'] as const : []),
    ...(!hasQrCodeCriteria ? ['codes'] as const : []),
    ...(isSilentAdventure ? ['display'] as const : []),
  ]

  // Editor menu props for mobile hamburger menu in workflow designer
  const editorMenuProps = {
    activeSection,
    onSectionChanged: handleSectionChange,
    onEditorSave: handleSave,
    onPublish: handlePublish,
    onDelete,
    onHistory: handleHistory,
    onFlowVersionHistory: handleFlowVersionHistory,
    onImportFlow: handleImportFlow,
    isEditorSaving: isSaving,
    isPublishing,
    isReadyToPublish,
    hiddenSections: computedHiddenSections,
    workflowAllowed: workflowAllowedForCategory,
  }

  // Get adventureId from store for collaboration
  const storeAdventureId = useAdventureEditorStore((state) => state.adventureId)
  const collaborationAdventureId = storeAdventureId || adventureId || null

  return (
    <AdventureCollaborationProvider adventureId={collaborationAdventureId}>
      <CollaborativeEditorReceiver />
      <FieldChangeIndicatorProvider>
        <Box
          className="adventure-editor-root"
          sx={{
            width: '100%',
            p: '0 !important',
            px: '0 !important',
            m: '0 !important',
            overflow: 'visible',
          }}
        >
        {/* Error Dialog */}
        {error && (
          <ErrorDialog
            open={true}
            title={error.title}
            message={error.message}
            details={error.details}
            onClose={() => setError(null)}
          />
        )}

        {/* Hidden file input for flow import */}
        <input
          ref={flowImportInputRef}
          type="file"
          accept=".json"
          onChange={handleFlowFileChange}
          style={{ display: 'none' }}
        />

        {/* Navigation Ribbon - hidden on mobile when in workflow editor */}
        {showRibbon && (
            <AdventureRibbon
              activeSection={activeSection}
              onSectionChanged={handleSectionChange}
              onSave={handleSave}
              onPublish={handlePublish}
              onDelete={onDelete}
              onHistory={handleHistory}
              onFlowVersionHistory={handleFlowVersionHistory}
              onImportFlow={handleImportFlow}
              isSaving={isSaving}
              isPublishing={isPublishing}
              isReadyToPublish={isReadyToPublish}
              workflowAllowed={workflowAllowedForCategory}
              hiddenSections={computedHiddenSections}
            />
        )}

        {/* Collaborators Indicator - shows connected users (hidden in workflow mode as it has its own) */}
        {activeSection !== 'locationflow' && (
          <CollaboratorsIndicator />
        )}

        {/* Content Panels */}
        <Box
          sx={{
            p: (activeSection === 'locationflow' || activeSection === 'mapview') ? 0 : '15px',
            overflow: 'visible',
            ...((activeSection === 'locationflow' || activeSection === 'mapview') && {
              height: '100%',
              minHeight: 400,
            }),
          }}
        >
          <AdventureContentPanels ref={contentPanelsRef} editorMenuProps={editorMenuProps} />
        </Box>

        {/* History Dialog */}
        {collaborationAdventureId && (
          <AdventureHistoryDialog
            open={historyDialogOpen}
            onClose={handleHistoryClose}
            adventureId={collaborationAdventureId}
            adventureTitle={generalInfo.title}
          />
        )}

        {/* Flow Version History Dialog */}
        {collaborationAdventureId && (
          <FlowVersionHistoryDialog
            open={flowVersionHistoryOpen}
            onClose={handleFlowVersionHistoryClose}
            adventureId={collaborationAdventureId}
            currentVersionNumber={useWorkflowStore.getState().versionNumber}
            getCurrentWorkingState={useWorkflowStore.getState().nodes.length > 0
              ? useWorkflowStore.getState().getSerializableState
              : null}
          />
        )}
        </Box>
      </FieldChangeIndicatorProvider>
    </AdventureCollaborationProvider>
  )
}
